import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

import { findNodeHandle } from '@/utils/findNodeHandler';

import type { ScrollMetrics } from './types';

const isScroller = (node: unknown): node is HTMLElement =>
  typeof node === 'object' && node !== null && 'scrollHeight' in node;

export const useScrollOverflowObserver = (
  scrollViewRef: RefObject<unknown>,
  enabled: boolean,
  onMetrics: (metrics: ScrollMetrics) => void,
): void => {
  const onMetricsRef = useRef(onMetrics);
  onMetricsRef.current = onMetrics;

  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return;
    if (!scrollViewRef.current) return;
    const node = findNodeHandle(
      scrollViewRef.current as Parameters<typeof findNodeHandle>[0],
    );
    if (!isScroller(node)) return;

    const report = () =>
      onMetricsRef.current({
        offsetY: node.scrollTop,
        contentHeight: node.scrollHeight,
        layoutHeight: node.clientHeight,
      });
    const observer = new ResizeObserver(report);
    observer.observe(node, { box: 'border-box' });
    if (node.firstElementChild) {
      observer.observe(node.firstElementChild, { box: 'border-box' });
    }
    report();
    return () => observer.disconnect();
  }, [enabled, scrollViewRef]);
};
